import { Avatar } from "@chakra-ui/react";
import type { Participant } from "livekit-client";
import React, { useLayoutEffect, useRef, useState } from "react";
import colors from "../../../app/colors";
import UserAvatar from "../../_shared/user-avatar";
import { useUsers } from "../users-provider";

function VideoCallParticipantAvatarBody({
  userId,
  displayName,
  px,
}: {
  userId: string;
  displayName: string;
  px: number;
}) {
  const { usersById } = useUsers();
  const user = usersById[userId];
  const box = `${px}px`;
  const radius = `${Math.floor(px / 2)}px`;

  if (user) {
    return (
      <UserAvatar
        userId={userId}
        boxSizePx={box}
        borderRadiusPx={radius}
        displayOnlineIndicator={false}
      />
    );
  }

  return (
    <Avatar
      name={displayName}
      boxSize={box}
      w={box}
      h={box}
      borderRadius={radius}
      bg={colors.gray0}
      color={colors.gray30}
      fontWeight={700}
    />
  );
}

/**
 * Fills the LiveKit camera-off placeholder: participant avatar at 50% of min(tile width, height).
 */
export function VideoCallParticipantPlaceholderAvatar({
  participant,
}: {
  participant: Participant;
}) {
  const measureRef = useRef<HTMLDivElement>(null);
  const [avatarPx, setAvatarPx] = useState(0);

  useLayoutEffect(() => {
    const el = measureRef.current;
    if (!el) return;

    const update = () => {
      const r = el.getBoundingClientRect();
      const d = Math.min(r.width, r.height);
      setAvatarPx(Math.max(0, Math.floor(d * 0.5)));
    };

    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const userId = participant.identity;
  const displayName = participant.name || participant.identity;

  return (
    <div
      ref={measureRef}
      className="video-call-participant-placeholder-measure"
    >
      {avatarPx > 0 ? (
        <VideoCallParticipantAvatarBody
          userId={userId}
          displayName={displayName}
          px={avatarPx}
        />
      ) : null}
    </div>
  );
}
